import React, { useCallback, useEffect, useRef, useState } from "react"
import { Badge, Button, Card, Col, Form, Row, Spinner, Table } from "react-bootstrap"
import { toast } from "react-toastify"
import API from "../../common/API"

interface BatchStats {
  processed?: number
  created?: number
  skipped?: number
  matched?: number
  ambiguous?: number
  notMatched?: number
  preAssigned?: number
  errors: number
}

interface ServiceStatus {
  name: string
  description: string
  lastRunAt: string | null
  lastError: string | null
  lastBatch: BatchStats
}

const AdminSystemMonitorComponent = () => {
  const [services, setServices] = useState<ServiceStatus[]>([])
  const [loading, setLoading] = useState(false)
  const [running, setRunning] = useState<Record<string, boolean>>({})
  const [emailTo, setEmailTo] = useState("")
  const [emailSubject, setEmailSubject] = useState("Test email desde Dataworker")
  const [emailBody, setEmailBody] = useState("Este es un correo de prueba enviado desde el panel de administración.")
  const [sendingEmail, setSendingEmail] = useState(false)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const fetchStatus = useCallback(async () => {
    try {
      const res = await API.getAction("SystemMonitor/Status")
      if (res.data.status === "ok") setServices(res.data.response.services)
    } catch { /* silent */ }
  }, [])

  useEffect(() => {
    fetchStatus()
    intervalRef.current = setInterval(fetchStatus, 10000)
    return () => { if (intervalRef.current) clearInterval(intervalRef.current) }
  }, [fetchStatus])

  const runService = async (endpoint: string, label: string) => {
    setRunning(r => ({ ...r, [endpoint]: true }))
    try {
      const res = await API.postAction(`SystemMonitor/${endpoint}`, {})
      if (res.data.status === "ok") {
        toast.success(`${label}: ${JSON.stringify(res.data.response)}`)
        await fetchStatus()
      } else {
        toast.error(`Error: ${res.data.response}`)
      }
    } catch (ex: any) {
      toast.error(ex.message)
    } finally {
      setRunning(r => ({ ...r, [endpoint]: false }))
    }
  }

  const sendTestEmail = async () => {
    if (!emailTo.trim()) { toast.error("Ingresa un destinatario"); return }
    setSendingEmail(true)
    try {
      const res = await API.postAction("SystemMonitor/TestEmail", { to: emailTo, subject: emailSubject, body: emailBody })
      if (res.data.status === "ok") toast.success(res.data.response)
      else toast.error(res.data.response)
    } catch (ex: any) {
      toast.error(ex.message)
    } finally {
      setSendingEmail(false)
    }
  }

  const formatDate = (d: string | null) => {
    if (!d) return <span className="text-muted">Nunca</span>
    return new Date(d).toLocaleString("es-DO")
  }

  const serviceEndpoint: Record<string, string> = {
    FichadosAutoSync: "RunFichadosAutoSync",
    PartesCedulaMatch: "RunPartesCedulaMatch",
  }

  const renderBatch = (b: BatchStats) => (
    <div className="d-flex flex-wrap gap-2 mt-2">
      {b.processed !== undefined && <Badge bg="secondary">Procesados: {b.processed}</Badge>}
      {b.created !== undefined && <Badge bg="success">Creados: {b.created}</Badge>}
      {b.skipped !== undefined && <Badge bg="light" text="dark">Saltados: {b.skipped}</Badge>}
      {b.matched !== undefined && <Badge bg="success">Matched: {b.matched}</Badge>}
      {b.ambiguous !== undefined && <Badge bg="warning" text="dark">Ambiguos: {b.ambiguous}</Badge>}
      {b.notMatched !== undefined && <Badge bg="danger">No match: {b.notMatched}</Badge>}
      {b.preAssigned !== undefined && <Badge bg="info">Pre-asig: {b.preAssigned}</Badge>}
      {b.errors > 0 && <Badge bg="danger">Errores: {b.errors}</Badge>}
    </div>
  )

  return (
    <div className="p-3">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h5 className="mb-0"><i className="fas fa-server me-2" />Monitor del Sistema</h5>
        <Button variant="outline-secondary" size="sm" onClick={fetchStatus} disabled={loading}>
          <i className="fas fa-sync-alt me-1" />Actualizar
        </Button>
      </div>

      {/* Background Services */}
      <h6 className="text-muted mb-2">Procesos en Background</h6>
      <Row className="g-3 mb-4">
        {services.map(svc => {
          const ep = serviceEndpoint[svc.name]
          const isRunning = running[ep]
          const hasError = !!svc.lastError
          return (
            <Col md={6} key={svc.name}>
              <Card className={`h-100 ${hasError ? "border-danger" : "border-0 shadow-sm"}`}>
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div>
                      <strong>{svc.name}</strong>
                      <div className="text-muted small">{svc.description}</div>
                    </div>
                    <Badge bg={svc.lastRunAt ? (hasError ? "danger" : "success") : "secondary"}>
                      {svc.lastRunAt ? (hasError ? "Error" : "OK") : "Sin correr"}
                    </Badge>
                  </div>

                  <Table size="sm" borderless className="mb-2 small">
                    <tbody>
                      <tr>
                        <td className="text-muted ps-0">Último run</td>
                        <td>{formatDate(svc.lastRunAt)}</td>
                      </tr>
                    </tbody>
                  </Table>

                  {renderBatch(svc.lastBatch)}

                  {hasError && (
                    <div className="alert alert-danger py-1 px-2 small mt-2 mb-0">
                      <i className="fas fa-exclamation-circle me-1" />
                      {svc.lastError}
                    </div>
                  )}

                  {ep && (
                    <Button
                      variant="primary"
                      size="sm"
                      className="mt-3 w-100"
                      onClick={() => runService(ep, svc.name)}
                      disabled={isRunning}
                    >
                      {isRunning
                        ? <><Spinner size="sm" className="me-1" />Ejecutando...</>
                        : <><i className="fas fa-play me-1" />Ejecutar ahora</>}
                    </Button>
                  )}
                </Card.Body>
              </Card>
            </Col>
          )
        })}
      </Row>

      {/* Email Test */}
      <h6 className="text-muted mb-2">Test de Correo</h6>
      <Card className="border-0 shadow-sm">
        <Card.Body>
          <Row className="g-2">
            <Col md={4}>
              <Form.Label className="small fw-semibold">Destinatario</Form.Label>
              <Form.Control
                size="sm"
                type="email"
                placeholder="correo@ejemplo.com"
                value={emailTo}
                onChange={e => setEmailTo(e.target.value)}
              />
            </Col>
            <Col md={4}>
              <Form.Label className="small fw-semibold">Asunto</Form.Label>
              <Form.Control
                size="sm"
                value={emailSubject}
                onChange={e => setEmailSubject(e.target.value)}
              />
            </Col>
            <Col md={4} className="d-flex align-items-end">
              <Button
                variant="outline-primary"
                size="sm"
                className="w-100"
                onClick={sendTestEmail}
                disabled={sendingEmail}
              >
                {sendingEmail
                  ? <><Spinner size="sm" className="me-1" />Enviando...</>
                  : <><i className="fas fa-paper-plane me-1" />Enviar Test</>}
              </Button>
            </Col>
            <Col md={12}>
              <Form.Label className="small fw-semibold">Cuerpo</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                size="sm"
                value={emailBody}
                onChange={e => setEmailBody(e.target.value)}
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </div>
  )
}

export default AdminSystemMonitorComponent
